<template>
  <div class="min-h-screen flex items-center justify-center p-4 bg-gray-50">
    <div class="relative max-w-md w-full">
      <div class="bg-white rounded-xl shadow-lg p-8 transition-all hover:shadow-xl">
        <div class="text-center mb-8">
          <div class="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
            </svg>
          </div>
          <h2 class="text-3xl font-bold text-gray-900 mb-2">Join Our Platform</h2>
          <p class="text-gray-600">Create your customer account</p>
        </div>

        <form @submit.prevent="handleRegister" class="space-y-6">
          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <input
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              :class="{ 'border-red-500': fieldErrors.email }"
              placeholder="Enter your email"
              @blur="validateEmail"
            />
            <p v-if="fieldErrors.email" class="text-red-500 text-xs mt-1">{{ fieldErrors.email }}</p>
          </div>

          <!-- Phone Field -->
          <div>
            <label for="phone" class="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span class="text-gray-500">+254</span>
              </div>
              <input
                id="phone"
                v-model="phoneDigits"
                name="phone"
                type="tel"
                autocomplete="tel"
                required
                class="w-full px-4 py-3 pl-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                :class="{ 'border-red-500': fieldErrors.phone }"
                placeholder="712345678"
                maxlength="9"
                @input="handlePhoneInput"
                @blur="validatePhone"
              />
            </div>
            <p class="text-xs text-gray-500 mt-1">Format: +254712345678</p>
            <p v-if="fieldErrors.phone" class="text-red-500 text-xs mt-1">{{ fieldErrors.phone }}</p>
          </div>

          <!-- Business Name -->
          <div>
            <label for="business_name" class="block text-sm font-semibold text-gray-700 mb-2">Business Name</label>
            <input
              id="business_name"
              v-model="form.business_name"
              name="business_name"
              type="text"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              :class="{ 'border-red-500': fieldErrors.business_name }"
              placeholder="Your business name"
              @blur="validateBusinessName"
            />
            <p v-if="fieldErrors.business_name" class="text-red-500 text-xs mt-1">{{ fieldErrors.business_name }}</p>
          </div>

          <!-- Account Type -->
          <input type="hidden" name="role" v-model="form.role" value="customer" required />
          <!-- <div>
            <label for="role" class="block text-sm font-semibold text-gray-700 mb-2">Account Type</label>
            <select
              id="role"
              v-model="form.role"
              name="role"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white"
              :class="{ 'border-red-500': fieldErrors.role }"
            >
              <option value="">Select account type</option>
              <option value="admin_vendor">Admin Vendor (Full Access)</option>
              <option value="sales_vendor">Sales Vendor (POS Access)</option>
              <option value="customer">Customer (Browse & Order)</option>
            </select>
            <p v-if="fieldErrors.role" class="text-red-500 text-xs mt-1">{{ fieldErrors.role }}</p>
          </div> -->

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-semibold text-gray-700 mb-2">Password</label>
            <input
              id="password"
              v-model="form.password"
              name="password"
              type="password"
              autocomplete="new-password"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              :class="{ 'border-red-500': fieldErrors.password }"
              placeholder="Create a strong password"
              @input="validatePassword"
              @blur="validatePassword"
            />
            <div class="mt-2">
              <p class="text-xs text-gray-500 mb-2">8+ characters with uppercase, lowercase, number & special character</p>
              <div class="grid grid-cols-2 gap-1 text-xs">
                <span :class="passwordRequirements.length ? 'text-green-600' : 'text-red-500'">
                  ✓ 8+ characters
                </span>
                <span :class="passwordRequirements.uppercase ? 'text-green-600' : 'text-red-500'">
                  ✓ Uppercase letter
                </span>
                <span :class="passwordRequirements.lowercase ? 'text-green-600' : 'text-red-500'">
                  ✓ Lowercase letter
                </span>
                <span :class="passwordRequirements.number ? 'text-green-600' : 'text-red-500'">
                  ✓ Number
                </span>
                <span :class="passwordRequirements.special ? 'text-green-600' : 'text-red-500'">
                  ✓ Special character
                </span>
              </div>
            </div>
            <p v-if="fieldErrors.password" class="text-red-500 text-xs mt-1">{{ fieldErrors.password }}</p>
          </div>

          <!-- Success/Error Messages -->
          <div v-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {{ error }}
          </div>

          <div v-if="success" class="p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
            {{ success }}
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="authStore.loading || !isFormValid"
            class="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="authStore.loading" class="flex items-center justify-center">
              <div class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Creating account...
            </span>
            <span v-else>Create Account</span>
          </button>

          <!-- Sign In Link -->
          <div class="text-center pt-4 border-t border-gray-200">
            <p class="text-gray-600 text-sm mb-3">Already have an account?</p>
            <router-link 
              to="/login" 
              class="inline-block w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors border border-gray-300"
            >
              Sign In
            </router-link>
          </div>
        </form>
      </div>

      <div class="mt-8 text-center">
        <p class="text-sm text-gray-600">
          © 2025 Retailer Management System
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import type { RegisterData } from '@/stores/auth'

const authStore = useAuthStore()

const form = reactive<RegisterData>({
  email: '',
  phone: '',
  password: '',
  business_name: '',
  role: 'admin_vendor'
})

const phoneDigits = ref('')
const error = ref('')
const success = ref('')

const fieldErrors = reactive<{[key: string]: string}>({})
const passwordRequirements = reactive({
  length: false,
  uppercase: false,
  lowercase: false,
  number: false,
  special: false
})

// Computed properties for validation
const isPasswordValid = computed(() => {
  return Object.values(passwordRequirements).every(req => req === true)
})

const isFormValid = computed(() => {
  return form.email && 
         form.phone && 
         form.business_name && 
         form.role && 
         isPasswordValid.value &&
         Object.keys(fieldErrors).length === 0
})

// Phone input handling
function handlePhoneInput() {
  // Only allow numbers and limit to 9 digits
  phoneDigits.value = phoneDigits.value.replace(/\D/g, '').slice(0, 9)
  form.phone = '+254' + phoneDigits.value
}

// Validation functions
function validateEmail() {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!form.email) {
    fieldErrors.email = 'Email is required'
  } else if (!emailRegex.test(form.email)) {
    fieldErrors.email = 'Please enter a valid email address'
  } else {
    delete fieldErrors.email
  }
}

function validatePhone() {
  if (!phoneDigits.value) {
    fieldErrors.phone = 'Phone number is required'
  } else if (phoneDigits.value.length !== 9) {
    fieldErrors.phone = 'Phone number must be 9 digits after +254'
  } else {
    delete fieldErrors.phone
  }
}

function validateBusinessName() {
  if (!form.business_name) {
    fieldErrors.business_name = 'Business name is required'
  } else if (form.business_name.length < 2) {
    fieldErrors.business_name = 'Business name must be at least 2 characters'
  } else {
    delete fieldErrors.business_name
  }
}

function validatePassword() {
  // Check password requirements
  passwordRequirements.length = form.password.length >= 8
  passwordRequirements.uppercase = /[A-Z]/.test(form.password)
  passwordRequirements.lowercase = /[a-z]/.test(form.password)
  passwordRequirements.number = /[0-9]/.test(form.password)
  passwordRequirements.special = /[@$!%*?&]/.test(form.password)

  if (!form.password) {
    fieldErrors.password = 'Password is required'
  } else if (!isPasswordValid.value) {
    fieldErrors.password = 'Password does not meet all requirements'
  } else {
    delete fieldErrors.password
  }
}

async function handleRegister() {
  error.value = ''
  success.value = ''
  
  // Run all validations
  validateEmail()
  validatePhone()
  validateBusinessName()
  validatePassword()

  // Check if form is valid
  if (!isFormValid.value) {
    error.value = 'Please fix all form errors before submitting'
    return
  }

  try {
    const response = await authStore.register(form)
    success.value = response.message
    
    // Reset form
    Object.assign(form, {
      email: '',
      phone: '',
      password: '',
      business_name: '',
      role: 'admin_vendor'
    })
    phoneDigits.value = ''
    
    // Reset password requirements
    Object.keys(passwordRequirements).forEach(key => {
      passwordRequirements[key as keyof typeof passwordRequirements] = false
    })
    
  } catch (err: unknown) {
    const apiError = (err as { error?: unknown })?.error
    error.value =
      typeof apiError === 'string'
        ? apiError
        : 'Registration failed. Please check your information.'
  }
}
</script>

<style scoped>
/* Custom select arrow */
select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
